/**
 * LiveOpsErrorBoundary — captures React render crashes, reports to /api/live/error,
 * shows a graceful "Something went wrong" fallback with reload + reset.
 */
import { Component } from "react";
import { emitLiveError } from "@/hooks/useLiveOps";

export default class LiveOpsErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: error?.message || "Render crashed" };
  }

  componentDidCatch(error, info) {
    try {
      emitLiveError("react_crash", error, {
        component: info?.componentStack?.split("\n").map((s) => s.trim()).filter(Boolean)[0]?.slice(0, 80),
        componentStack: (info?.componentStack || "").slice(0, 1500),
      });
    } catch (_) {}
  }

  reset = () => this.setState({ hasError: false, message: null });

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6"
             data-testid="live-ops-error-boundary">
          <div className="max-w-md w-full text-center space-y-4">
            <div className="text-5xl">🛟</div>
            <h1 className="font-serif text-2xl font-semibold">Something went wrong</h1>
            <p className="text-sm text-muted-foreground">
              We've logged the issue and will look at it shortly. You can try reloading or going back home.
            </p>
            {this.state.message && (
              <pre className="text-[11px] text-muted-foreground bg-muted/40 rounded-md p-2 text-left overflow-x-auto">
                {this.state.message}
              </pre>
            )}
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-xl text-sm font-medium"
                data-testid="error-boundary-reload"
              >
                Reload
              </button>
              <button
                onClick={() => { this.reset(); window.location.assign("/dashboard"); }}
                className="px-4 py-2 bg-muted text-foreground rounded-xl text-sm font-medium"
                data-testid="error-boundary-home"
              >
                Go home
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
